import { Card, Col, Row, Select, Skeleton, Tag } from "antd";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";

const { Option } = Select;

const widgets = [
    {
        title: "Story Points",
        key: "story_points",
        color: "var(--color-primary)",
        icon: "⚡",
    },
    {
        title: "Open Tickets",
        key: "open_tickets",
        color: "var(--color-error)",
        icon: "📂",
    },
    {
        title: "In Progress",
        key: "progress_tickets",
        color: "var(--color-warning)",
        icon: "🔄",
    },
    {
        title: "Ready for Testing",
        key: "ready_for_qa",
        color: "var(--color-warning)",
        icon: "🧪",
    },
    {
        title: "In Testing",
        key: "in_qa_tickets",
        color: "var(--color-primary-hover)",
        icon: "🔬",
    },
    {
        title: "Closed",
        key: "closed_tickets",
        color: "var(--color-success)",
        icon: "✅",
    },
    {
        title: "Total Tickets",
        key: "total_tickets",
        color: "var(--color-primary)",
        icon: "🎫",
    },
];

const ticketTypeColors = {
    tickets: "var(--color-text)",
    bug_tickets: "var(--color-error)",
    enhancement_tickets: "var(--color-primary-hover)",
    problem_tickets: "var(--color-warning)",
    task_tickets: "var(--color-success)",
};

const columns = [
    {
        title: "Assignee",
        dataIndex: "assignee_name",
        width: "22%",
        render: (name) => (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                    style={{
                        width: 32,
                        height: 32,
                        borderRadius: 10,
                        background: "var(--color-primary-light)",
                        border: "1.5px solid var(--color-primary)",
                        color: "var(--color-primary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 13,
                        flexShrink: 0,
                    }}
                >
                    {name ? name[0].toUpperCase() : "?"}
                </div>
                <span
                    style={{
                        color: "var(--color-text)",
                        fontWeight: 500,
                        fontSize: 13,
                    }}
                >
                    {name}
                </span>
            </div>
        ),
    },
    {
        title: "Tickets",
        dataIndex: "tickets",
        render: (v) => (
            <span style={{ color: ticketTypeColors.tickets, fontWeight: 600 }}>
                {v ?? "—"}
            </span>
        ),
    },
    {
        title: "Bugs",
        dataIndex: "bug_tickets",
        render: (v) =>
            v ? (
                <Tag
                    style={{
                        background: "var(--color-error-light)",
                        border: "1px solid var(--color-error)",
                        color: "var(--color-error)",
                        borderRadius: 6,
                        fontWeight: 600,
                    }}
                >
                    {v}
                </Tag>
            ) : (
                <span style={{ color: "var(--color-text-muted)" }}>—</span>
            ),
    },
    {
        title: "Enhancements",
        dataIndex: "enhancement_tickets",
        render: (v) =>
            v ? (
                <Tag
                    style={{
                        background: "var(--color-primary-light)",
                        border: "1px solid var(--color-primary-hover)",
                        color: "var(--color-primary-hover)",
                        borderRadius: 6,
                        fontWeight: 600,
                    }}
                >
                    {v}
                </Tag>
            ) : (
                <span style={{ color: "var(--color-text-muted)" }}>—</span>
            ),
    },
    {
        title: "Problems",
        dataIndex: "problem_tickets",
        render: (v) =>
            v ? (
                <Tag
                    style={{
                        background: "var(--color-warning-light)",
                        border: "1px solid var(--color-warning)",
                        color: "var(--color-warning)",
                        borderRadius: 6,
                        fontWeight: 600,
                    }}
                >
                    {v}
                </Tag>
            ) : (
                <span style={{ color: "var(--color-text-muted)" }}>—</span>
            ),
    },
    {
        title: "Tasks",
        dataIndex: "task_tickets",
        render: (v) =>
            v ? (
                <Tag
                    style={{
                        background: "var(--color-success-light)",
                        border: "1px solid var(--color-success)",
                        color: "var(--color-success)",
                        borderRadius: 6,
                        fontWeight: 600,
                    }}
                >
                    {v}
                </Tag>
            ) : (
                <span style={{ color: "var(--color-text-muted)" }}>—</span>
            ),
    },
];

function KpiCard({ title, value, color, icon }) {
    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: `1px solid ${color}33`,
                borderRadius: 12,
                padding: "14px 16px",
                marginBottom: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
            }}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span
                    style={{
                        fontSize: 11,
                        color: "var(--color-text-muted)",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                    }}
                >
                    {title}
                </span>
                <span style={{ fontSize: 22, fontWeight: 700, color }}>
                    {value ?? "—"}
                </span>
            </div>
            <span
                style={{
                    fontSize: 22,
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: `${color}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {icon}
            </span>
        </div>
    );
}

function HealthCard({ label, value }) {
    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "16px 20px",
                margin: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
            }}
        >
            <span
                style={{
                    fontSize: 11,
                    color: "var(--color-text-muted)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    textAlign: "center",
                }}
            >
                {label}
            </span>
            <span
                style={{
                    fontSize: 26,
                    fontWeight: 700,
                    color: "var(--color-primary)",
                }}
            >
                {value ?? "—"}
            </span>
        </div>
    );
}

function SprintAnalytics() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [widgetsData, setWidgetsData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [healthData, setHealthData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [sprints, setSprints] = useState([]);

    const {
        status: sprintStatus,
        data: sprintRes,
        refetch: getSprints,
    } = useQuery(
        "jira-sprints",
        async () => await HTTP("/analytics/getJiraSprints", { staff_id }),
        { enabled: false },
    );

    const {
        status: sprintDetailStatus,
        data: sprintDetailRes,
        refetch: getSprintDetails,
    } = useQuery(
        "sprint-details",
        async () =>
            await HTTP("/analytics/getJiraSprintDetail", { ...formData }),
        { enabled: false },
    );

    useEffect(() => {
        getSprints();
    }, []);

    useEffect(() => {
        if (formData !== null) getSprintDetails();
    }, [formData]);

    useEffect(() => {
        if (sprintStatus === "success") {
            setSprints(sprintRes.data);
            setFormData({
                staff_id,
                sprint_id: sprintRes.data.find((s) => s.state === "active")
                    ?.sprint_id,
            });
        }
    }, [sprintStatus, sprintRes]);

    useEffect(() => {
        if (sprintDetailStatus === "success") {
            setTableData(sprintDetailRes.data.assignees_tickets);
            setWidgetsData(
                widgets.map((w) => ({
                    ...w,
                    value: sprintDetailRes.data[w.key],
                })),
            );
            setHealthData(sprintDetailRes.data.health_gadget);
        }
    }, [sprintDetailStatus, sprintDetailRes]);

    const isReady = (s) => s !== "idle" && s !== "loading";

    return (
        <Card className="dashboard-container">
            <CustomHeader />

            {/* Sprint selector */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "12px 0",
                    marginBottom: 16,
                }}
            >
                <Select
                    showSearch
                    loading={sprints.length === 0}
                    value={sprints.length > 0 ? formData?.sprint_id : undefined}
                    onChange={(sprint_id) =>
                        setFormData((p) => ({ ...p, sprint_id }))
                    }
                    style={{ minWidth: 280 }}
                    filterOption={(input, option) =>
                        option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                    placeholder="Select Sprint"
                >
                    {sprints.map((s) => (
                        <Option key={s.sprint_id} value={s.sprint_id}>
                            {s.state === "active"
                                ? `${s.sprint_name} — CURRENT`
                                : s.sprint_name}
                        </Option>
                    ))}
                </Select>
            </div>

            {/* Main content */}
            <Row gutter={[16, 16]}>
                {/* Assignee table */}
                <Col xs={24} lg={18}>
                    <div
                        style={{
                            background: "var(--color-bg-card)",
                            border: "1px solid var(--color-border)",
                            borderRadius: 12,
                            padding: "20px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginBottom: 16,
                            }}
                        >
                            <div
                                style={{
                                    width: 3,
                                    height: 18,
                                    borderRadius: 2,
                                    background:
                                        "var(--gradient-primary)",
                                }}
                            />
                            <span
                                style={{
                                    color: "var(--color-text)",
                                    fontWeight: 600,
                                    fontSize: 15,
                                }}
                            >
                                Assignee Details
                            </span>
                        </div>
                        {isReady(sprintDetailStatus) ? (
                            <CustomTable
                                columns={columns}
                                tableData={tableData}
                                rowKey="assignee_name"
                                isScroll
                            />
                        ) : (
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 10 }}
                            />
                        )}
                    </div>
                </Col>

                {/* KPI widgets */}
                <Col xs={24} lg={6}>
                    <div
                        style={{
                            background: "var(--color-bg-card)",
                            border: "1px solid var(--color-border)",
                            borderRadius: 12,
                            padding: "20px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginBottom: 16,
                            }}
                        >
                            <div
                                style={{
                                    width: 3,
                                    height: 18,
                                    borderRadius: 2,
                                    background:
                                        "var(--gradient-primary)",
                                }}
                            />
                            <span
                                style={{
                                    color: "var(--color-text)",
                                    fontWeight: 600,
                                    fontSize: 15,
                                }}
                            >
                                Ticket Details
                            </span>
                        </div>
                        {widgetsData.length > 0 ? (
                            widgetsData.map((w) => (
                                <KpiCard
                                    key={w.key}
                                    title={w.title}
                                    value={w.value}
                                    color={w.color}
                                    icon={w.icon}
                                />
                            ))
                        ) : (
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 14 }}
                            />
                        )}
                    </div>
                </Col>
            </Row>

            {/* Health analytics */}
            <div
                style={{
                    background: "var(--color-bg-card)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 12,
                    padding: "20px",
                    marginTop: 16,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 16,
                    }}
                >
                    <div
                        style={{
                            width: 3,
                            height: 18,
                            borderRadius: 2,
                            background:
                                "var(--gradient-primary)",
                        }}
                    />
                    <span
                        style={{
                            color: "var(--color-text)",
                            fontWeight: 600,
                            fontSize: 15,
                        }}
                    >
                        Health Analytics
                    </span>
                </div>
                {healthData?.length > 0 ? (
                    <Row gutter={[8, 8]}>
                        {healthData.slice(1).map((data, i) => (
                            <Col xs={12} sm={8} md={6} key={i}>
                                <HealthCard label={data[0]} value={data[1]} />
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <Skeleton active title={false} paragraph={{ rows: 4 }} />
                )}
            </div>
        </Card>
    );
}

export { SprintAnalytics };
